import { PORTAL_ROLE } from "./portal";
import { mergeTemplateCallDefaults, normalizeTemplateCallDefaults } from "./templateCallCatalog";

const JSON_HEADERS = {
  "Content-Type": "application/json",
};

let foundationAccessToken = "";

const PRODUCTION_CAT_AI_ORIGIN = "https://cartoon.ykq5580.com";

function isLocalBrowserHost() {
  return /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(window.location.hostname);
}

function localHttpOrigin(port) {
  // 本地开发端口只在浏览器运行时拼接，避免公网构建包写入完整本机地址。
  return `${window.location.protocol}//${["127", "0", "0", "1"].join(".")}:${port}`;
}

function catAiOrigin() {
  if (typeof window === "undefined") return PRODUCTION_CAT_AI_ORIGIN;
  return isLocalBrowserHost()
    ? localHttpOrigin(8099)
    : PRODUCTION_CAT_AI_ORIGIN;
}

export function setFoundationAccessToken(accessToken) {
  foundationAccessToken = String(accessToken || "").trim();
}

export function clearFoundationAccessToken() {
  foundationAccessToken = "";
}

async function request(path, options = {}) {
  const { omitPortalRole = false, ...fetchOptions } = options;
  const isFormData = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  // 每个请求都携带门户角色，供后端执行权限判断和数据隔离。
  const response = await fetch(path, {
    ...fetchOptions,
    headers: {
      ...(isFormData ? {} : JSON_HEADERS),
      ...(omitPortalRole ? {} : { "X-Document-Hub-Role": PORTAL_ROLE }),
      ...(foundationAccessToken ? { Authorization: "Bearer " + foundationAccessToken } : {}),
      ...(fetchOptions.headers || {}),
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || response.statusText || "服务请求失败");
    error.status = response.status;
    error.payload = payload;
    error.path = path;
    throw error;
  }
  return payload;
}

export function getManagedTemplates(params = {}, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return request("/api/document-templates/managed?" + query.toString(), { signal });
}

export function getSourceTemplateCatalog(signal) {
  return request("/api/document-templates/source-catalog", { signal });
}

export function getSourceTemplate(sourceId, signal) {
  return request("/api/document-templates/source-templates/" + encodeURIComponent(sourceId), { signal });
}

export function importSourceTemplate(sourceId, options = {}) {
  return request("/api/document-templates/source-templates/" + encodeURIComponent(sourceId) + "/import", {
    method: "POST",
    body: JSON.stringify(options),
  });
}

export function getManagedTemplateBySource(sourceDocumentId, signal) {
  return request("/api/document-templates/managed/by-source/" + encodeURIComponent(sourceDocumentId), { signal });
}

export function getTemplateVersions(templateId, signal) {
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/versions", { signal });
}

export function getTemplateVersion(versionId, signal) {
  return request("/api/document-templates/versions/" + encodeURIComponent(versionId), { signal });
}

export function createManagedTemplate(metadata, file) {
  const body = new FormData();
  Object.entries(metadata).forEach(([key, value]) => {
    if (value !== undefined && value !== null) body.set(key, String(value));
  });
  body.set("file", file, file.name);
  return request("/api/document-templates/templates", { method: "POST", body });
}

export function createTemplateVersion(templateId, file, requiresElectronicSignature) {
  const body = new FormData();
  body.set("requiresElectronicSignature", String(Boolean(requiresElectronicSignature)));
  body.set("file", file, file.name);
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/versions", {
    method: "POST",
    body,
  });
}

export function createTemplateDraft(templateId) {
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/drafts", {
    method: "POST",
    body: "{}",
  });
}

export function setTemplateCategoryDefault(templateId) {
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/category-default", {
    method: "PUT",
    body: "{}",
  });
}

export function clearTemplateCategoryDefault(templateId) {
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/category-default", {
    method: "DELETE",
  });
}

export function updateTemplateClassification(templateId, classification) {
  return request("/api/document-templates/templates/" + encodeURIComponent(templateId) + "/classification", {
    method: "PUT",
    body: JSON.stringify(classification),
  });
}

export function saveTemplateVersionContent(versionId, content) {
  return request("/api/document-templates/versions/" + encodeURIComponent(versionId) + "/content", {
    method: "PUT",
    body: JSON.stringify(content),
  });
}

export function saveVersionSignatureConfig(versionId, signatureConfig) {
  return request("/api/document-templates/versions/" + encodeURIComponent(versionId) + "/signature-config", {
    method: "PUT",
    body: JSON.stringify(signatureConfig),
  });
}

export function saveTemplateVersionSettings(versionId, settings) {
  return request("/api/document-templates/versions/" + encodeURIComponent(versionId) + "/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export function publishTemplateVersion(versionId) {
  return request("/api/document-templates/versions/" + encodeURIComponent(versionId) + "/publish", {
    method: "POST",
    body: "{}",
  });
}

export async function fetchTemplateVersionFile(versionId, kind = "rendered-pdf", signal) {
  const response = await fetch(
    "/api/document-templates/versions/" + encodeURIComponent(versionId) + "/" + kind,
    {
      signal,
      headers: {
        "X-Document-Hub-Role": PORTAL_ROLE,
        ...(foundationAccessToken ? { Authorization: "Bearer " + foundationAccessToken } : {}),
      },
    },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || "模板文件读取失败");
  }
  return response.blob();
}

export async function getPublishedTemplateBySource(sourceDocumentId, signal) {
  try {
    const managed = await getManagedTemplateBySource(sourceDocumentId, signal);
    return managed?.publishedVersion || null;
  } catch {
    return null;
  }
}

export function getCurrentCompany(signal) {
  return request("/api/company/current", { signal });
}

export function getTemplateOverrides(params, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return request("/api/document-templates/templates?" + query.toString(), { signal });
}

export async function getTemplateCallDefaultTemplates(params = {}, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  query.set("templateType", "system");
  query.set("isDefaultTemplate", "true");
  query.set("includeContent", "true");

  const origin = typeof window !== "undefined" && isLocalBrowserHost()
    ? localHttpOrigin(18082)
    : "";
  // 模板调用服务的跨域白名单不接收文书平台角色头；认证令牌仍由 request 统一携带。
  const payload = await request(`${origin}/api/v1/templates?${query.toString()}`, {
    signal,
    omitPortalRole: true,
  });
  return normalizeTemplateCallDefaults(payload.items || payload.templates || []);
}

function abortError() {
  const error = new Error("请求已取消");
  error.name = "AbortError";
  return error;
}

export async function getTemplateOverridesWithDefaults(params = {}, signal) {
  const [overrideResult, defaultResult] = await Promise.allSettled([
    getTemplateOverrides(params, signal),
    getTemplateCallDefaultTemplates(params, signal),
  ]);
  if (signal?.aborted) throw abortError();

  const overridePayload = overrideResult.status === "fulfilled" ? overrideResult.value : null;
  const defaultTemplates = defaultResult.status === "fulfilled" ? defaultResult.value : [];
  if (!overridePayload && defaultResult.status === "rejected") {
    throw overrideResult.reason || defaultResult.reason;
  }

  const existingTemplates = overridePayload?.templates || overridePayload?.items || [];
  const templates = mergeTemplateCallDefaults(existingTemplates, defaultTemplates);
  return {
    ...(overridePayload || {}),
    templates,
    items: templates,
    total: templates.length,
    defaultTemplate: templates.find((template) => template.templateCallDefault)
      || overridePayload?.defaultTemplate
      || null,
  };
}

export function sendAiChat(payload, signal) {
  // AI 外观和大模型代理统一由卡通猫项目承载；本分系统只注入文书模板业务上下文。
  return request(`${catAiOrigin()}/api/ai/chat`, {
    method: "POST",
    // 统一 AI 网关按 scene 路由到文书模板业务 AI，避免串到其他分系统。
    headers: { "X-AI-Scene": "templates" },
    body: JSON.stringify(payload),
    signal,
  });
}

export function sendAiFeedback(payload) {
  return request("/api/ai/feedback", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createAiTask(payload) {
  return request("/api/ai/tasks", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function runAiOptimization() {
  return request("/api/ai/optimize/run", {
    method: "POST",
    body: "{}",
  });
}

export function getAiMemories(params = {}, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return request("/api/ai/memories?" + query.toString(), { signal });
}

function normalizeTemplateAssetPath(filePath) {
  return String(filePath || "")
    .split("/")
    .filter((part) => part && part !== "." && part !== "..")
    .join("/");
}

export function templateDownloadUrl(filePath) {
  // 丢弃相对路径片段并逐段编码，避免模板路径被用于目录穿越。
  const safePath = normalizeTemplateAssetPath(filePath)
    .split("/")
    .map(encodeURIComponent)
    .join("/");
  return "/" + safePath;
}

export function templateRenderPdfEndpoint(documentId) {
  return "/api/document-templates/" + encodeURIComponent(documentId || "") + "/render-pdf";
}

export function renderTemplatePdf(file, params = {}, signal) {
  const documentId = file?.id || "";
  return request(templateRenderPdfEndpoint(documentId), {
    method: "POST",
    body: JSON.stringify({
      documentId,
      params,
      sourceSystem: "document-template-hub",
      moduleName: file?.moduleName || file?.submoduleName || file?.scenarioName || "",
      filePath: file?.filePath || "",
    }),
    signal,
  });
}
